import React from "react";
import { Link } from "gatsby";
import InfoStyles from "../styles/InfoStyles";
import TopPhotoBannerStyles from "../styles/TopPhotoBannerStyles";
import Seo from "../seo";
import { StaticImage } from "gatsby-plugin-image";

const TheoryOfChange = () => {
  return (
    <>
      <Seo
        title="Our impact"
        description="We're uniting stakeholders from across the industry to create the change we need."
        url="https://www.table-sage.com/theory-of-change"
        image="https://res.cloudinary.com/table-sage/image/upload/f_auto,q_auto:good/v1612360827/top-banners/2020-04-16_12.37.33_1_ikvnzh.jpg"
        imageWidth="500"
        imageHeight="375"
      />
      <TopPhotoBannerStyles
        style={{
          display: "grid",
        }}
      >
        <StaticImage
          style={{
            gridArea: "1/1",
            maxHeight: 500,
            minHeight: 500,
          }}
          layout="fullWidth"
          quality={80}
          // This is a presentational image, so the alt should be an empty string
          alt=""
          src={
            "https://res.cloudinary.com/table-sage/image/upload/f_auto,q_auto:good/v1612360827/top-banners/2020-04-16_12.37.33_1_ikvnzh.jpg"
          }
          loading="eager"
          placeholder="blurred"
        />
        <div
          style={{
            // By using the same grid area for both, they are stacked on top of each other
            gridArea: "1/1",
            position: "relative",
            background:
              "linear-gradient(rgba(35, 31, 32, 0.15), rgba(35, 31, 32, 0.15))",
          }}
        >
          <h1 className="header">Bridging critical gaps</h1>
          <h2 className="sub-header">
            We're uniting stakeholders from across the industry to create the
            change we need.
          </h2>
        </div>
      </TopPhotoBannerStyles>
      <InfoStyles>
        <div className="info">
          <h2 className="info__header-1 mt-6">
            Working Together for a Better Restaurant Industry
          </h2>
          <p className="info__body-text">
            We look beyond the food on our plates to create a dining industry
            with purpose. Our five core{" "}
            <Link to="/values" className="text-link">
              values
            </Link>
            —Cuisine, Experience, Story, Sustainability, and Diversity—were
            carefully designed to build a meaningful and impactful restaurant
            industry for diners, industry workers, and our natural environment.
            We want to see an industry that is not only based on these values,
            but that actively strives to embody them.
          </p>
          <p className="pull-quote">
            “88% of consumers [want] brands to help them live more sustainably”
          </p>
          <p className="info__body-text">
            <Link to="/about" className="text-link">
              Table Sage
            </Link>{" "}
            is working to bring about the changes we all want to see. Forbes
            writes that 88% of consumers want brands to help them live more
            sustainably, and we know that businesses with diverse management
            consistently outperform. That’s why we’re bridging the gap between
            stakeholders and empowering them to shape the future of the $3.4
            trillion global foodservice market [PR Newswire].
          </p>
          <StaticImage
            src="https://res.cloudinary.com/table-sage/image/upload/f_auto,q_auto:best/v1629714240/general-assets/2020-04-16_03.41.54_1_kl0dtr.jpg"
            alt="Sustainable café Coffee & Coconuts in Amsterdam attracts a steady
            stream of young, conscious consumers."
            placeholder="blur"
            objectFit="cover"
            objectPosition="center"
            className="info__img"
          />
          <p className="info__img-caption">
            Sustainable café Coffee & Coconuts in Amsterdam attracts a steady
            stream of young, conscious consumers.
          </p>

          <h2 className="info__header-1 pt-6">Empowering Diners</h2>
          <p className="info__body-text">
            Patrons have an incredible amount of power to change the restaurant
            industry. The key is accessing and channeling that power, for good.
            Table Sage’s proprietary{" "}
            <Link to="/locate" className="text-link">
              search
            </Link>{" "}
            page is designed to give diners the knowledge and tools to make
            decisions that reflect their values, showing the industry what they
            care about and what they want to see changed.
          </p>
          <p className="info__body-text">
            Also in the works: a plan to{" "}
            <Link to="/rate" className="text-link">
              incentivize and reward
            </Link>{" "}
            positive patron participation with donations to hunger projects.
          </p>
          <h2 className="info__header-1 pt-5">Supporting Stakeholders</h2>
          <p className="info__body-text">
            Table Sage gives diners the power to eat according to their values,
            but it also gives eateries and other stakeholders the chance to
            learn from the decisions diners make and the values they care about.
            By constructively sharing feedback with restaurants, we strive to
            inspire them to go beyond great food in their offerings and business
            models.
          </p>
          <p className="info__body-text">
            Every week, we select restaurants to{" "}
            <a
              rel="noreferrer noopener"
              target="_blank"
              href="https://www.instagram.com/tablesagedining/"
              className="text-link"
            >
              highlight
            </a>{" "}
            individually for their work.{" "}
            {/* On a bi-weekly basis, we publish more
            in-depth{" "}
            <Link to="/content/best-of/amsterdam" className="text-link">
              articles
            </Link>
            — either interviews with eatery leadership or collections of
            laudable restaurants around town.  */}
            Our close relationships with the eateries we work with means that
            we’re in a great position to connect them to the stakeholders that
            can best support their conscious intentions.
          </p>
          <StaticImage
            src="https://res.cloudinary.com/table-sage/image/upload/f_auto,q_auto:best/v1629635571/general-assets/P1133613_yjlybh.jpg"
            alt="Owner of Amsterdam hot spot, Labyrinth, sees his business as more
            than just a place for great cocktails, bites, and community events."
            placeholder="blur"
            objectFit="cover"
            objectPosition="center"
            className="info__img"
          />
          <p className="info__img-caption">
            Owner of Amsterdam hot spot, Labyrinth, sees his business as more
            than just a place for great cocktails, bites, and community events.
          </p>
          <h2 className="info__header-1 pt-6">
            Committing to Action and Thought Leadership
          </h2>
          <p className="info__body-text">
            Stakeholders across the food industry are asking the same questions:
            How do we reconcile the phenomena of widespread food waste with
            global starvation rates? How do we make sustainable the food
            production and consumption rates that Project Drawdown cites are
            responsible for about 25% of total greenhouse gases? And how do we
            increase ethnic and gender diversity across food service management
            from less than one third [USA Today], and include them within
            industry awards? To keep us committed to the future and our roles in
            making it brighter, Table Sage is{" "}
            <Link to="/partnerships" className="text-link">
              bringing stakeholders together
            </Link>
            .
          </p>
          <p className="pull-quote">
            “How do we reconcile the phenomena of widespread food waste with
            global starvation rates?”
          </p>
          <p className="info__body-text">
            Starting in our home city of Amsterdam, we’ve worked to find and
            implement solutions to dining industry-related challenges. Beginning
            with a unique survey and in-depth interviews, we sought to
            understand and support business owners in their journey to
            sustainability during COVID-19. The results of this first local
            industry scan from early 2021 can be found{" "}
            <Link
              to="/content/articles/amsterdam/restaurants-need-support-to-continue-sustainability-efforts-during-covid-19"
              className="text-link"
            >
              here
            </Link>
            .
          </p>
        </div>
        <p className="bottom-msg pb-8">
          <Link className="text-link" to="/locate">
            Search
          </Link>{" "}
          our curated restaurants to make a positive impact today, or{" "}
          <Link className="text-link" to="/partnerships">
            partner
          </Link>{" "}
          with us on a larger impact project.
        </p>
      </InfoStyles>
    </>
  );
};

export default TheoryOfChange;
